import React, { useEffect, useState } from 'react';
import {
  ButtonIcon,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { MAX_FILTER_COUNT } from './constant';
import { getActiveFiltersCount } from './utils';

function AddFilterDropdown({
  showFilterBar,
  isDropDownMenuOpen,
  headingDescription,
  placeholder,
  filters,
  setFilters,
  dropdownMenuPositionToRight,
  dropDownWrapperRef,
  filterListWrapRef,
  handleRemoveFilter,
  isMobileResponsivenessEnabled,
}) {
  const [searchText, setSearchText] = useState('');
  const [isVisible, setIsVisible] = useState(true);

  const addFilterDropdownClasses = classnames(
    'AddFilterDropdown-dropDownRef',
    isDropDownMenuOpen && showFilterBar
      ? 'AddFilterDropdown-dropDown--isActive'
      : 'AddFilterDropdown-dropDown--isDisabled',
    dropdownMenuPositionToRight && 'AddFilterDropdown-dropDownRight'
  );

  const onClose = () => {
    setIsVisible(false);
  };

  const enableFilter = (filterKey) => {
    if (getActiveFiltersCount(filters) >= MAX_FILTER_COUNT) {
      setIsVisible(true);

      return;
    }

    const prevFilters = JSON.parse(JSON.stringify(filters));

    setFilters(
      prevFilters.map((filter) => {
        if (filter.key === filterKey) {
          return {
            ...filter,
            isActive: !filter.isActive,
          };
        }

        return filter;
      })
    );
  };

  const disableFilter = (filterKey) => {
    handleRemoveFilter(filterKey);
    setIsVisible(false);
  };

  useEffect(() => {
    if (!dropDownWrapperRef?.current) return;

    const updateFocusableElements = () => {
      const focusableElements = dropDownWrapperRef.current
        .querySelectorAll(`.AddFilterDropdown-dropDownRef [href],
             .AddFilterDropdown-dropDownRef input,
             .AddFilterDropdown-dropDownRef select,
             .AddFilterDropdown-dropDownRef text,
             .AddFilterDropdown-dropDownRef textarea,
             .AddFilterDropdown-dropDownRef [tabindex]:not([tabindex="-1"]),
             .AddFilterDropdown-dropDownRef li`);

      focusableElements.forEach((el) => {
        if (!isDropDownMenuOpen) {
          // Store previous tabindex if not already stored
          if (!el.hasAttribute('data-prev-tabindex')) {
            el.setAttribute(
              'data-prev-tabindex',
              el.getAttribute('tabindex') || '0'
            );
          }

          // eslint-disable-next-line no-param-reassign
          el.tabIndex = -1;
        } else {
          const prevTabIndex = el.getAttribute('data-prev-tabindex');

          if (prevTabIndex !== null) {
            // eslint-disable-next-line no-param-reassign
            el.tabIndex = parseInt(prevTabIndex, 10);
            el.removeAttribute('data-prev-tabindex');
          } else {
            el.removeAttribute('tabindex');
          }
        }
      });
    };

    updateFocusableElements();

    // MutationObserver to detect when elements become visible
    const observer = new MutationObserver(() => {
      updateFocusableElements();
    });

    observer.observe(dropDownWrapperRef.current, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [isDropDownMenuOpen, dropDownWrapperRef]);

  if (isMobileResponsivenessEnabled) {
    return (
      <>
        {getActiveFiltersCount(filters) >= MAX_FILTER_COUNT && isVisible && (
          <div className='AddFilterDropdown-dropDownHeadingDescription--mobileResponsive'>
            <InlineMessage
              kind='background'
              size='small'
              text='You’ve reached maximum number of filter sets allowed. Remove existing ones to add more.'
              colorTheme='info'
              leftIcon={
                <Icon
                  kind='AlertInfoFilledSeparateColors'
                  color='--color-foreground-info-medium'
                />
              }
              isVisible={isVisible}
              onDismiss={onClose}
              isDismissible
            />
          </div>
        )}
        <div className='AddFilterDropdown-dropDownSearchBar--mobileResponsive'>
          <InputField
            name='basic'
            placeholder={placeholder}
            kind='search'
            size='large'
            prefixIcon={
              <Icon kind='search' color='neutral-700' size='smallMedium' />
            }
            value={searchText}
            setValue={setSearchText}
            tabIndex={0}
          />
        </div>
        <div
          className='AddFilterDropdown-dropDownFilterList--mobileResponsive'
          ref={filterListWrapRef}>
          {filters
            ?.filter(
              (filter) =>
                !filter?.isDefault &&
                filter.label.toLowerCase().indexOf(searchText.toLowerCase()) !==
                  -1 &&
                !filter?.isCustom
            )
            ?.sort((a, b) => b.isActive - a.isActive)
            ?.map((filter) => (
              <div
                key={filter?.key}
                className='AddFilterDropdown-dropDownFilterListItems--mobileResponsive'>
                {filter?.isActive ? (
                  <div
                    className='AddFilterDropdown-dropDownFilterListItemsWrapper--mobileResponsive'
                    onClick={() => {
                      disableFilter(filter?.key);
                    }}
                    onKeyUp={() => {}}
                    role='button'
                    tabIndex={0}>
                    <div className='AddFilterDropdown-dropDownFilterListItemsCustomButtom--isEnabled'>
                      <ButtonIcon
                        size='small'
                        kind='bold'
                        isSelected
                        icon={<Icon kind='Subtract' />}
                      />
                    </div>
                    <Typography
                      kind='body2-bold'
                      className='AddFilterDropdown-dropDownFilterListItemsCustomButtomText--isEnabled'>
                      {filter?.label}
                    </Typography>
                  </div>
                ) : (
                  <div
                    className='AddFilterDropdown-dropDownFilterListItemsWrapper--mobileResponsive'
                    onClick={() => {
                      enableFilter(filter?.key);
                    }}
                    onKeyUp={() => {}}
                    role='button'
                    tabIndex={0}>
                    <div className='AddFilterDropdown-dropDownFilterListItemsCustomButtom--isDisabled'>
                      <ButtonIcon
                        size='small'
                        kind='outlinedSecondary'
                        isDisabled={false}
                        icon={<Icon kind='Add' />}
                      />
                    </div>
                    <Typography
                      kind='body2'
                      className='AddFilterDropdown-dropDownFilterListItemsCustomButtomText--isDisabled'>
                      {filter?.label}
                    </Typography>
                  </div>
                )}
              </div>
            ))}
        </div>
      </>
    );
  }

  return (
    <div className={addFilterDropdownClasses}>
      <div className='AddFilterDropdown-dropDownHeadingDescription'>
        <Typography kind='body3'>{headingDescription}</Typography>
        {getActiveFiltersCount(filters) >= MAX_FILTER_COUNT && (
          <InlineMessage
            kind='background'
            size='small'
            text='You’ve reached maximum number of filter sets allowed. Remove existing ones to add more.'
            colorTheme='info'
            leftIcon={
              <Icon
                kind='AlertInfoFilledSeparateColors'
                color='--color-foreground-info-medium'
              />
            }
            isVisible={isVisible}
            onDismiss={onClose}
            isDismissible
          />
        )}
      </div>
      <div className='AddFilterDropdown-dropDownSearchBar'>
        <InputField
          name='basic'
          placeholder={placeholder}
          kind='search'
          size='small'
          prefixIcon={
            <Icon kind='search' color='neutral-700' size='smallMedium' />
          }
          value={searchText}
          setValue={setSearchText}
          tabIndex={isDropDownMenuOpen ? 0 : -1}
        />
      </div>
      <div
        className='AddFilterDropdown-dropDownFilterList'
        ref={filterListWrapRef}>
        {filters
          ?.filter(
            (filter) =>
              !filter?.isDefault &&
              filter.label.toLowerCase().indexOf(searchText.toLowerCase()) !==
                -1 &&
              !filter?.isCustom
          )
          ?.sort((a, b) => b.isActive - a.isActive)
          ?.map((filter) => (
            <div
              key={filter?.key}
              className='AddFilterDropdown-dropDownFilterListItems'>
              {filter?.isActive ? (
                <li
                  className='AddFilterDropdown-dropDownFilterListItemsWrapper'
                  onClick={() => {
                    disableFilter(filter?.key);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      disableFilter(filter?.key);
                    }
                  }}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role='button'
                  tabIndex={0}>
                  <div className='AddFilterDropdown-dropDownFilterListItemsCustomButtom--isEnabled'>
                    <ButtonIcon
                      size='small'
                      kind='bold'
                      isSelected
                      tabIndex={-1}
                      icon={<Icon kind='Subtract' />}
                    />
                  </div>
                  <Typography
                    kind='body2-bold'
                    className='AddFilterDropdown-dropDownFilterListItemsCustomButtomText--isEnabled'>
                    {filter?.label}
                  </Typography>
                </li>
              ) : (
                <li
                  className='AddFilterDropdown-dropDownFilterListItemsWrapper'
                  onClick={() => {
                    enableFilter(filter?.key);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      enableFilter(filter?.key);
                    }
                  }}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role='button'
                  tabIndex={0}>
                  <div className='AddFilterDropdown-dropDownFilterListItemsCustomButtom--isDisabled'>
                    <ButtonIcon
                      size='small'
                      kind='outlinedSecondary'
                      isDisabled={false}
                      tabIndex={-1}
                      icon={<Icon kind='Add' />}
                    />
                  </div>
                  <Typography
                    kind='body2'
                    className='AddFilterDropdown-dropDownFilterListItemsCustomButtomText--isDisabled'>
                    {filter?.label}
                  </Typography>
                </li>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

AddFilterDropdown.propTypes = {
  showFilterBar: PropTypes.bool.isRequired,
  isDropDownMenuOpen: PropTypes.bool.isRequired,
  headingDescription: PropTypes.string,
  placeholder: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  setFilters: PropTypes.func.isRequired,
  dropdownMenuPositionToRight: PropTypes.bool,
  dropDownWrapperRef: PropTypes.shape({
    current: PropTypes.oneOf([PropTypes.shape({}), null]),
  }).isRequired,
  filterListWrapRef: PropTypes.shape({}).isRequired,
  handleRemoveFilter: PropTypes.func.isRequired,
  isMobileResponsivenessEnabled: PropTypes.bool,
};

AddFilterDropdown.defaultProps = {
  headingDescription: '',
  placeholder: '',
  dropdownMenuPositionToRight: false,
  isMobileResponsivenessEnabled: false,
};

export default AddFilterDropdown;
